import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  OnDestroy,
  Self,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
  viewChild,
  ViewContainerRef
} from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { Timestamp } from "@angular/fire/firestore"
import {
  FormBuilder,
  FormsModule,
  NgControl,
  ReactiveFormsModule
} from "@angular/forms"
import { MatDatepickerModule } from "@angular/material/datepicker"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { Subject } from "rxjs"

import { ensureDate } from "~types"

@Component({
  selector: "app-fs-datetime",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatIconModule,
    MatInputModule
  ],
  template: `
    <ng-template #template>
      <mat-form-field [formGroup]="parts">
        <mat-label>{{ prefix }}Date</mat-label>
        <input
          matInput
          [matDatepicker]="datepicker"
          formControlName="date"
          name="date" />
        <mat-datepicker-toggle
          matIconSuffix
          [for]="datepicker"></mat-datepicker-toggle>
        <mat-datepicker #datepicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field [formGroup]="parts">
        <mat-label>{{ prefix }}Time</mat-label>
        <input matInput #time type="time" formControlName="time" name="time" />
      </mat-form-field>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: none;
      }
    `
  ],
  host: {
    "[class.floating]": "shouldLabelFloat",
    "[id]": "id"
  }
  // providers: [
  //   {
  //     provide: NG_VALUE_ACCESSOR,
  //     multi: true,
  //     useExisting: forwardRef(() => FSDatetimeInput)
  //   }
  // ]
})
export class FSDatetimeInput implements OnDestroy {
  formbuilder: FormBuilder = inject(FormBuilder)
  readonly parts = this.formbuilder.group({
    date: new Date(),
    time: "00:00"
  })

  @Input() prefix: string = ""

  readonly dateInput = viewChild.required<HTMLInputElement>("date")
  readonly timeInput = viewChild.required<HTMLInputElement>("time")

  readonly stateChanges = new Subject<void>()
  touched = false
  readonly controlType = "app-fs-datetime"

  readonly _required = input<boolean, unknown>(false, {
    alias: "required",
    transform: booleanAttribute
  })
  readonly _disabledByInput = input<boolean, unknown>(false, {
    alias: "disabled",
    transform: booleanAttribute
  })

  onChange: (ts: Timestamp | null) => void = () => {}
  onTouched = () => {}

  private readonly _disabledByCva = signal(false)
  private readonly _disabled = computed(
    () => this._disabledByInput() || this._disabledByCva()
  )

  get empty() {
    const {
      value: { date, time }
    } = this.parts

    return !date && !time
  }

  get required(): boolean {
    return this._required()
  }

  get disabled(): boolean {
    return this._disabled()
  }

  private _value: Date | null = null
  get value() {
    return this._value
  }

  get errorState(): boolean {
    return this.parts.invalid && this.touched
  }
  @ViewChild("template", { static: true }) template!: TemplateRef<unknown>

  constructor(
    @Self() public ngControl: NgControl,
    private viewContainerRef: ViewContainerRef
  ) {
    this.ngControl.valueAccessor = this

    effect(() => {
      // Read signals to trigger effect.
      this._required()
      this._disabled()
      // Propagate state changes.
      untracked(() => this.stateChanges.next())
    })

    effect(() => {
      if (this._disabled()) {
        untracked(() => this.parts.disable())
      } else {
        untracked(() => this.parts.enable())
      }
    })

    effect(() => {
      const [date, time] = this.timeGroupBits(this.value)
      untracked(() => this.parts.setValue({ date, time }))
    })

    this.parts.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.stateChanges.next()
    })

    this.parts.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      const date = this.parts.valid
        ? this.timeGroupBits(this.dateFrom(value))[0]
        : null
      this._updateValue(date)
    })
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template)
  }
  dateFrom(props?: Partial<{ date: Date | null; time: string | null }>) {
    props = props || this.parts.value
    const date = props.date || new Date()
    const [hours, minutes] = (props.time || "00:00").split(":").map(Number)
    date.setHours(hours)
    date.setMinutes(minutes)
    return date
  }

  ngOnDestroy() {
    this.stateChanges.complete()
  }

  writeValue(ts: Timestamp | Date | null): void {
    this._updateValue(ts)
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled)
  }

  private _updateValue(dateish: Timestamp | Date | null) {
    const [date, time] = this.timeGroupBits(dateish)

    this._value = date
    this.parts.setValue({ date, time }, { onlySelf: true, emitEvent: false })
    this.onChange(Timestamp.fromDate(date))
  }

  timeGroupBits(timestamp?: Timestamp | Date | null): [Date, string] {
    const date = ensureDate(timestamp)

    return [date, date.toTimeString().slice(0, 5)]
  }
}
